import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AIIcon } from '../AI-Icon';
import { Icon } from '../Icon';
import { faqAssistanceService } from '../../../services/faqAssistanceService';
import { sanitizeHtml } from '../../../utils/sanitize';
import { toApiLanguageCode } from '../../../utils/languageMapper';
import './carolina-chat-panel.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface CarolinaChatPanelProps {
  isOpen: boolean;
  isClosing: boolean;
  onClose: () => void;
}

export default function CarolinaChatPanel({ isOpen, isClosing, onClose }: CarolinaChatPanelProps) {
  const { t, i18n } = useTranslation();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const chatScrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      const timer = setTimeout(() => textareaRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Auto-resize textarea
  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setChatInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  }, []);

  const handleSend = useCallback(async (text?: string) => {
    const trimmed = (text ?? chatInput).trim();
    if (!trimmed || isLoading) return;

    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: trimmed };
    const assistantMsgId = crypto.randomUUID();
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setIsLoading(true);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Create empty assistant message that will be filled via streaming
    setChatMessages(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: '' }]);

    try {
      await faqAssistanceService.askQuestionStream(trimmed, {
        onChunk: (chunk) => {
          setIsLoading(false);
          setChatMessages(prev =>
            prev.map(msg =>
              msg.id === assistantMsgId
                ? { ...msg, content: msg.content + chunk }
                : msg
            )
          );
        },
        onRestore: () => {
          setChatMessages(prev =>
            prev.map(msg =>
              msg.id === assistantMsgId
                ? { ...msg, content: '' }
                : msg
            )
          );
          setIsLoading(true);
        },
        onDone: () => {
          setIsLoading(false);
        },
        onError: () => {
          setChatMessages(prev =>
            prev.map(msg =>
              msg.id === assistantMsgId
                ? { ...msg, content: t('aiSidebar.errorMessage') }
                : msg
            )
          );
          setIsLoading(false);
        },
      }, undefined, toApiLanguageCode(i18n.language));
    } catch {
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === assistantMsgId
            ? { ...msg, content: t('aiSidebar.errorMessage') }
            : msg
        )
      );
      setIsLoading(false);
    }
  }, [chatInput, isLoading, t, i18n.language]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  if (!isOpen) return null;

  const showHero = chatMessages.length === 0;
  const predefinedQuestions = [
    t('aiSidebar.question1'),
    t('aiSidebar.question2'),
    t('aiSidebar.question3'),
  ];

  return (
    <div className={`carolina-chat-panel${isClosing ? ' carolina-chat-panel--closing' : ''}`}>

      {/* Header — only when there are messages */}
      {!showHero && (
        <div className="carolina-chat-panel__fab-group">
          <div className="carolina-chat-panel__fab-actions">
            <button
              type="button"
              className="carolina-chat-panel__close-fab"
              title={t('aiSidebar.newChat')}
              onClick={() => { setChatMessages([]); setChatInput(''); }}
            >
              <Icon name="plus" size={18} />
            </button>
            <button type="button" className="carolina-chat-panel__close-fab" title={t('common.close')} onClick={onClose}>
              <Icon name="x" size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Hero section — only when no messages */}
      {showHero && (
        <div className="carolina-chat-panel__hero">
          <button type="button" className="carolina-chat-panel__hero-close" title={t('common.close')} onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
          
          <p className="carolina-chat-panel__hero-text">
            {t('aiSidebar.heroText')}
          </p>
        </div>
      )}

      {/* Messages */}
      {!showHero && (
        <div className="carolina-chat-panel__messages" ref={chatScrollRef}>
          {chatMessages.map((msg) => {
            if (msg.role === 'assistant' && !msg.content && isLoading) return null;
            return (
              <div key={msg.id} className={`carolina-chat-panel__bubble carolina-chat-panel__bubble--${msg.role}`}>
                <div className="carolina-chat-panel__bubble-content">
                  {msg.role === 'assistant'
                    ? <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.content) }} />
                    : msg.content
                  }
                </div>
              </div>
            );
          })}
          {isLoading && (
            <div className="carolina-chat-panel__bubble carolina-chat-panel__bubble--assistant">
              <div className="carolina-chat-panel__bubble-content carolina-chat-panel__typing">
                <span /><span /><span />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Predefined questions */}
      {showHero && (
        <div className="carolina-chat-panel__chips">
          {predefinedQuestions.map((q) => (
            <button
              key={q}
              type="button"
              className="carolina-chat-panel__chip"
              onClick={() => handleSend(q)}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="carolina-chat-panel__input-area">
        <div className="carolina-chat-panel__input-row">
          <textarea
            ref={textareaRef}
            className="carolina-chat-panel__textarea"
            value={chatInput}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder={t('aiSidebar.inputPlaceholder')}
            rows={1}
          />
          <button
            type="button"
            className="carolina-chat-panel__send-btn"
            onClick={() => handleSend()}
            disabled={!chatInput.trim() || isLoading}
          >
            <Icon name="send" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
